import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import FilterIcon from '@atlaskit/icon/core/migration/filter';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SortIcon, HideIcon } from '@databases/icons';
import { DatabaseFilter, DatabaseFilterUsageType } from '@databases/types/database/view';
import { getHiddenFields } from '@databases/utils/database/view/view';
import React, { FunctionComponent, ReactElement, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { useViewUiState } from '../../hooks/useViewUiState';
import { messages } from '../../i18n';
import { ViewPanelSettingType, ViewSettingConfigurationType } from '../../types/types';
import { getLayoutIcons } from '../../utils/utils';

const configurationRowStyles = xcss({
  paddingTop: 'space.100',
  paddingBottom: 'space.100',
  borderBottomWidth: '2px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'color.border',
});

const buttonBoxStyles = xcss({
  marginLeft: 'space.negative.150',
  marginRight: 'space.negative.150',
});

const buttonStyles = xcss({
  paddingTop: 'space.100',
  paddingBottom: 'space.100',
  paddingLeft: 'space.150',
  paddingRight: 'space.150',
});

const withoutCountStyles = xcss({
  color: 'color.text.disabled',
});

const withCountStyles = xcss({
  color: 'color.text',
});

const titleAndCountInlineStyles = xcss({
  flexWrap: 'wrap',
  gap: 'space.050',
  flex: 1,
});

export interface ViewSettingsConfigurationRowProps {
  viewSetting: ViewSettingConfigurationType;
}

const ViewSettingsConfigurationRow: FunctionComponent<
  ViewSettingsConfigurationRowProps
> = ({ viewSetting }) => {
  const { formatMessage } = useIntl();
  const { view } = useView();
  const { setActivePanelSetting } = useViewUiState();

  const getViewSettingsConfigurationProps = useCallback(
    (
      viewSetting: ViewSettingConfigurationType
    ): {
      icon: ReactElement;
      title: string;
      showCount: boolean;
      isNotZero?: boolean;
      countText?: string;
    } => {
      const filters = view.settings.filter as {
        type: DatabaseFilterUsageType;
        value: DatabaseFilter[];
      };
      const layoutIcons = getLayoutIcons(view.settings.layout.type, 'medium');

      switch (viewSetting) {
        case ViewPanelSettingType.FILTER:
          return {
            icon: <FilterIcon spacing="spacious" label="" />,
            title: formatMessage(messages['view-settings.filter.filter-text']),
            showCount: true,
            isNotZero: !!filters,
            countText: formatMessage(
              messages['view-settings-panel.filter.filter-count'],
              {
                n: filters ? filters.value.length : 0,
              }
            ),
          };
        case ViewPanelSettingType.LAYOUT:
          return {
            icon: layoutIcons.icon,
            title: formatMessage(
              messages['view-settings-panel.layout.layout-options-text'],
              {
                layoutType: formatMessage(layoutIcons.label),
              }
            ),
            showCount: false,
          };
        case ViewPanelSettingType.SORT:
          return {
            icon: <SortIcon label="" />,
            title: formatMessage(messages['view-settings.sort.sort-text']),
            showCount: false,
            // TODO NCTD-1499 add sort count
          };
        case ViewPanelSettingType.FIELDS:
          return {
            icon: <HideIcon label="" />,
            title: formatMessage(messages['view-settings.hide-field.field-text']),
            showCount: true,
            isNotZero: !!view.settings.fields,
            countText: formatMessage(
              messages['view-settings-panel.hide-field.field-count'],
              {
                n: view.settings.fields ? getHiddenFields(view.settings).length : 0,
              }
            ),
          };
      }
    },
    [formatMessage, view.settings]
  );

  const { icon, title, showCount, isNotZero, countText } =
    getViewSettingsConfigurationProps(viewSetting);

  return (
    <Box
      xcss={[configurationRowStyles, buttonBoxStyles]}
      testId={`view-settings-configuration-row-${viewSetting}`}
    >
      <Button
        appearance="subtle"
        spacing="none"
        shouldFitContainer={true}
        onClick={() => setActivePanelSetting(viewSetting)}
        testId={`view-settings-configuration-row-button-${viewSetting}`}
      >
        <Inline spread="space-between" alignBlock="center" xcss={buttonStyles}>
          <Inline alignBlock="center" xcss={xcss({ paddingRight: 'space.150' })}>
            {icon}
          </Inline>
          <Inline
            alignBlock="center"
            spread="space-between"
            xcss={titleAndCountInlineStyles}
          >
            <Inline space="space.200" alignBlock="center">
              <Heading as="h5" size="xsmall">
                {title}
              </Heading>
            </Inline>
            <Inline space="space.100" alignBlock="center">
              {showCount && countText && (
                <Heading as="div" size="xxsmall">
                  <Box
                    testId={`view-settings-configuration-row-count-${viewSetting}`}
                    xcss={isNotZero ? withCountStyles : withoutCountStyles}
                  >
                    {countText}
                  </Box>
                </Heading>
              )}
            </Inline>
          </Inline>

          <ChevronRightIcon
            spacing="spacious"
            color={token('color.icon.subtle', '#626F86')}
            label=""
            testId={`view-settings-configuration-row-chevron-${viewSetting}`}
          />
        </Inline>
      </Button>
    </Box>
  );
};

export default ViewSettingsConfigurationRow;
